import { Link, useDictionary, useLocale, useTranslator } from "gemi/client";
import { folioAiShowFreePromise } from "@/app/utils/landingPromise";
import { trackEvent } from "../utils/analytics";
import {
  META_PIXEL_ID_CLIENT,
  reopenConsentBanner,
} from "../utils/consentAnalytics";
import { font } from "./_shared";
import { siteFooterV2Dict } from "./SiteFooterV2.i18n";

const SITE_LINKS = [
  { href: "/", key: "home" },
  { href: "/pricing", key: "pricing" },
  { href: "/about", key: "about" },
  { href: "/contact", key: "contact" },
] as const;

const LEGAL_LINKS = [
  { href: "/privacy-policy", key: "privacy" },
  { href: "/terms-and-conditions", key: "terms" },
  // The cookie disclosure lives in the privacy policy ("Cookies and similar"),
  // so the cookie link points there rather than at a dead "#".
  { href: "/privacy-policy", key: "cookie" },
] as const;

const SOCIAL = [
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/quantuslabs",
    icon: "linkedin",
  },
  { name: "X", href: "https://x.com/Quantus_Labs", icon: "x" },
  {
    name: "Instagram",
    href: "https://www.instagram.com/getfolioai",
    // The Turkish market runs a separate Instagram account; every other
    // locale shares the getfolioai handle above.
    trHref: "https://www.instagram.com/folio_ai",
    icon: "instagram",
  },
] as const;

export function SiteFooterV2() {
  const t = useTranslator("SiteFooterV2");
  const tNav = useTranslator("SiteNavV2");
  const tFooter = useDictionary(siteFooterV2Dict);
  const siteLinks = [
    ...SITE_LINKS.map((l) => ({ ...l, label: tNav(l.key) })),
    { href: "/support-and-faq", key: "faq", label: tFooter("faq") },
  ] as const;
  const [locale] = useLocale();

  return (
    <footer
      className="relative w-full overflow-hidden bg-[#0a0a0a] px-5 py-16 lg:overflow-visible lg:px-16"
      style={font.body}
    >
      {/* mobile decor */}
      <img
        src="/v2/footer/decor-2.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute -left-16 top-5 z-0 h-[291px] w-[215px] rounded-[15px] object-cover lg:hidden"
      />
      <img
        src="/v2/footer/decor-1.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute -left-5 bottom-[130px] z-0 h-[221px] w-[163px] rounded-[10px] object-cover lg:hidden"
      />
      <img
        src="/v2/footer/decor-3.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute -right-5 top-1/2 z-0 h-[122px] w-[163px] rounded-[10px] object-cover lg:hidden"
      />
      {/* desktop decor — exact Figma geometry (footer 4114:2679, 1440-wide design). Two photos peek up
          into the CTA→footer gap (negative top); two flank the card / giant wordmark. All sit behind content (z-0). */}
      <img
        src="/v2/footer/d-topleft.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute z-0 hidden h-[122px] w-[163px] rounded-[10px] object-cover lg:left-[195px] lg:-top-[94px] lg:block"
      />
      <img
        src="/v2/footer/d-chair.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute z-0 hidden h-[326px] w-[241px] rounded-[10px] object-cover lg:right-[32px] lg:-top-[298px] lg:block"
      />
      <img
        src="/v2/footer/d-leftmid.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute z-0 hidden h-[221px] w-[163px] rounded-[10px] object-cover lg:-left-[31px] lg:top-[469px] lg:block"
      />
      <img
        src="/v2/footer/d-bottomright.jpg"
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute z-0 hidden h-[286px] w-[382px] rounded-[10px] object-cover lg:-right-[158px] lg:top-[739px] lg:block"
      />

      <div className="relative z-10 mx-auto flex max-w-[1312px] flex-col items-center gap-20">
        <div className="flex w-full flex-col gap-20 rounded-[20px] bg-[#1e1e22] p-[30px] lg:gap-12 lg:rounded-[30px] lg:p-16">
          <div className="flex flex-col items-center gap-12 text-center lg:flex-row lg:items-start lg:justify-between lg:text-left">
            {/* Left: headline + store badges + trust */}
            <div className="flex flex-col items-center gap-12 lg:max-w-[493px] lg:items-start">
              <div className="flex w-full flex-col gap-5">
                <h3
                  className="m-0 text-[30px] font-medium leading-[1.1] tracking-[-1.2px] text-[#e9dcd3]"
                  style={font.display}
                >
                  {t("card_title")}
                </h3>
                <p className="m-0 text-[16px] leading-[1.3] text-white/80">
                  {t("card_blurb")}
                </p>
              </div>
              <div className="flex flex-col items-center gap-4 lg:flex-row">
                <a
                  href="/app-store"
                  className="flex items-center gap-3 rounded-[10px] border border-white px-3 py-2 no-underline"
                >
                  <img
                    src="/v2/footer/appstore-icon.svg"
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    className="h-[33px] w-[27px]"
                  />
                  <span className="text-left leading-tight text-white">
                    <span className="block text-[10px]">
                      {t("download_appstore")}
                    </span>
                    <span className="block text-[20px] font-medium">
                      App Store
                    </span>
                  </span>
                </a>
                <a
                  href="/play-store"
                  className="flex items-center gap-3 rounded-[10px] border border-white px-3 py-2 no-underline"
                >
                  <img
                    src="/v2/footer/google-play-icon.svg"
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    className="size-[30px]"
                  />
                  <span className="text-left leading-tight text-white">
                    <span className="block text-[10px]">
                      {t("download_googleplay")}
                    </span>
                    <span className="block text-[20px] font-medium">
                      Google Play
                    </span>
                  </span>
                </a>
              </div>
              {folioAiShowFreePromise() && (
                <span className="flex items-center gap-1.5 text-[14px] tracking-[0.14px] text-white opacity-60">
                  <img
                    src="/v2/icons/star-light.svg"
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    className="size-5"
                  />
                  {t("trust_credits")}
                </span>
              )}
            </div>

            {/* Right: nav links + legal (desktop) + social */}
            <div className="flex w-full flex-col items-center gap-12 lg:w-auto lg:flex-row lg:items-start lg:gap-16">
              <div className="flex flex-col items-center lg:items-start">
                {siteLinks.map((l, i) => (
                  <Link
                    key={l.key}
                    href={l.href}
                    onClick={
                      l.key === "pricing"
                        ? () =>
                            trackEvent({
                              event: "pricing_click",
                              params: { from: "footer" },
                            })
                        : undefined
                    }
                    className={`py-3 text-[16px] no-underline ${i === 0 ? "font-medium text-white" : "text-white/60"}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
              {/* Same py-3 per-link rhythm as the site column so the rows line up:
                  home↔privacy, pricing↔terms, about↔cookie; contact and FAQ run on alone. */}
              <div className="hidden flex-col lg:flex">
                {LEGAL_LINKS.map((l) => (
                  <a
                    key={l.key}
                    href={l.href}
                    className="py-3 text-[16px] text-white underline opacity-40"
                  >
                    {t(l.key)}
                  </a>
                ))}
              </div>
              <div className="flex lg:flex-col">
                {SOCIAL.map((s) => (
                  <a
                    key={s.name}
                    href={
                      "trHref" in s && locale === "tr-TR" ? s.trHref : s.href
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    className="flex size-14 items-center justify-center"
                  >
                    <img
                      src={`/v2/footer/${s.icon}.svg`}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      decoding="async"
                      className="size-12"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-3 rounded-full bg-[#b0aeb9] px-10 py-[18px] sm:flex-row sm:justify-between">
            <span className="flex flex-col items-center gap-2 sm:flex-row sm:gap-5">
              <span className="text-[16px] font-medium text-[#343339] opacity-70">
                {t("copyright")}
              </span>
              {/* Consent-withdrawal surface (GDPR 7(3)): reopens the cookie banner so a decided visitor
                  can change their choice. Shown only when the Meta Pixel is configured, matching the
                  banner — with no pixel there is no consent-gated cookie to manage. */}
              {META_PIXEL_ID_CLIENT && (
                <button
                  type="button"
                  onClick={reopenConsentBanner}
                  className="text-[14px] font-medium text-[#343339] underline opacity-70"
                >
                  {t("cookie_preferences")}
                </button>
              )}
            </span>
            <span className="flex items-center gap-2 text-[14px] font-medium text-[#343339] opacity-70">
              {t("credit_before") && <span>{t("credit_before")}</span>}
              <img
                src="/v2/footer/quantus-labs.svg"
                alt="Quantus Labs"
                loading="lazy"
                decoding="async"
                className="h-[22px] w-auto"
              />
              {t("credit_after") && <span>{t("credit_after")}</span>}
            </span>
          </div>
        </div>

        <img
          src="/v2/footer/folio-wordmark.svg"
          alt="Folio AI"
          loading="lazy"
          decoding="async"
          // Intrinsic size (from the SVG's own viewBox) reserves the box height
          // before the lazy asset arrives, so this large last-element wordmark
          // does not pop in. width/height set the ratio; the classes still size it.
          width={332}
          height={85}
          className="h-auto w-full max-w-[331px] lg:max-w-[1308px]"
        />
      </div>
    </footer>
  );
}
